import {
  AlertCircle,
  Armchair,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  Clock3,
  LayoutTemplate,
  MessageSquareText,
  RefreshCw,
  ShoppingBag,
  TableProperties,
  Utensils,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import DashboardKpiCard from "../components/dashboard/DashboardKpiCard";
import DashboardOverviewSkeleton from "../components/dashboard/DashboardOverviewSkeleton";
import MenuStatusSection from "../components/dashboard/MenuStatusSection";
import OnboardingChecklist from "../components/dashboard/OnboardingChecklist";
import RecentOrdersSection from "../components/dashboard/RecentOrdersSection";
import SubscriptionStatusCard from "../components/dashboard/SubscriptionStatusCard";
import useRestaurantDashboard from "../hooks/useRestaurantDashboard";

import { formatPersianNumber as numberFormatter } from "../utils/persianNumber";
const dateFormatter = new Intl.DateTimeFormat("fa-IR", { month: "short", day: "numeric" });
const todayFormatter = new Intl.DateTimeFormat("fa-IR", { weekday: "long", day: "numeric", month: "long" });

const toPersianNumber = (value) => numberFormatter(Number(value) || 0);

const formatReservationDate = (reservation) => {
  const rawDate = reservation?.reservationDate || reservation?.date;
  if (!rawDate) return "";
  const date = new Date(`${rawDate}T12:00:00`);
  return Number.isNaN(date.getTime()) ? rawDate : dateFormatter.format(date);
};

// Grid column count for the stat strip, keyed by how many stats are visible.
const statColumns = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
};

const HeaderAction = ({ icon: Icon, label, to, primary = false }) => (
  <Link
    to={to}
    className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-bold transition-colors focus:outline-none focus:ring-4 focus:ring-primary/15 ${
      primary
        ? "bg-primary text-white hover:bg-[#e8731f]"
        : "border border-gray-200 bg-white text-accent hover:border-primary/40 hover:bg-gray-50"
    }`}
  >
    <Icon size={16} aria-hidden="true" />
    <span>{label}</span>
  </Link>
);

const attentionIcons = {
  reservation: CalendarDays,
  order: ShoppingBag,
  table: Armchair,
  menu: Utensils,
  message: MessageSquareText,
};

const AttentionItem = ({ item }) => {
  const Icon = attentionIcons[item.type] || AlertCircle;
  const content = (
    <>
      <Icon size={16} className="shrink-0 text-amber-600" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-700">
        {item.count ? `${toPersianNumber(item.count)} ${item.label}` : item.label}
      </span>
      {item.route && <ChevronLeft size={16} className="shrink-0 text-gray-300" aria-hidden="true" />}
    </>
  );

  const className = "flex min-h-10 items-center gap-2.5 rounded-lg px-2";

  if (item.route) {
    return (
      <Link
        to={item.route}
        className={`${className} transition-colors hover:bg-amber-50/60 focus:outline-none focus:ring-4 focus:ring-amber-100`}
      >
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
};

const AttentionPanel = ({ items }) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-2" aria-labelledby="attention-title">
    <h2 id="attention-title" className="flex items-center gap-2 px-2 pb-1 pt-1.5 text-sm font-extrabold text-accent">
      نیازمند توجه
      {items.length > 0 && (
        <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-bold leading-5 text-amber-800">
          {toPersianNumber(items.length)}
        </span>
      )}
    </h2>
    {items.length ? (
      <div className="divide-y divide-gray-100">
        {items.map((item) => (
          <AttentionItem key={item.id} item={item} />
        ))}
      </div>
    ) : (
      <p className="flex items-center gap-2 px-2 pb-2 pt-1 text-sm text-secondary">
        <CheckCircle2 size={16} className="shrink-0" aria-hidden="true" />
        همه چیز مرتب است.
      </p>
    )}
  </section>
);

export default function RestaurantDashboard() {
  const {
    data,
    status,
    loading,
    refreshing,
    resourceLoading,
    errors,
    refresh,
    actions,
    actionState,
  } = useRestaurantDashboard();

  if (loading) return <DashboardOverviewSkeleton />;

  if (status === "error") {
    return (
      <section className="mx-auto mt-10 max-w-lg rounded-2xl border border-red-100 bg-white p-6 text-center" dir="rtl">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <AlertCircle size={24} aria-hidden="true" />
        </span>
        <h1 className="mt-4 text-lg font-extrabold text-accent">داشبورد بارگذاری نشد</h1>
        <p className="mt-2 text-sm leading-6 text-gray-500">ارتباط با سرویس‌های داشبورد برقرار نشد. دوباره تلاش کنید.</p>
        <button
          type="button"
          onClick={refresh}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white focus:outline-none focus:ring-4 focus:ring-primary/20"
        >
          <RefreshCw size={17} aria-hidden="true" />
          تلاش دوباره
        </button>
      </section>
    );
  }

  const kpis = [];
  if (data.capabilities.reservations) {
    kpis.push({
      id: "reservations",
      icon: CalendarDays,
      label: "رزروهای امروز",
      value: toPersianNumber(data.today.reservationCount),
      secondary: data.today.reservationCount ? "مشاهده و مدیریت" : "رزروی ثبت نشده",
      to: "/dashboard/reservations",
    });
    kpis.push({
      id: "guests",
      icon: Users,
      label: "مهمان‌های امروز",
      value: `${toPersianNumber(data.today.guestCount)} نفر`,
      secondary: data.today.guestCount ? "از رزروهای فعال" : "مهمانی ثبت نشده",
    });
  }
  if (data.capabilities.tableAvailability && data.today.tables) {
    const tables = data.today.tables;
    kpis.push({
      id: "tables",
      icon: Armchair,
      label: "میزهای اشغال",
      value: tables.total ? `${toPersianNumber(tables.occupied)} از ${toPersianNumber(tables.total)}` : "—",
      secondary: tables.total
        ? `${toPersianNumber(tables.available)} آماده · ${toPersianNumber(tables.reserved)} رزرو`
        : "هنوز میزی تعریف نشده",
      to: "/dashboard/reservations?section=LIVE_FLOOR",
    });
  }
  if (data.capabilities.orders) {
    kpis.push({
      id: "orders",
      icon: ShoppingBag,
      label: "سفارش‌های جدید",
      value: toPersianNumber(data.today.pendingOrderCount),
      secondary: data.today.pendingOrderCount ? "در انتظار رسیدگی" : "سفارش جدیدی نیست",
      to: "/dashboard/orders",
    });
  }
  if (data.capabilities.reservations) {
    const nextReservation = data.today.nextReservation;
    kpis.push({
      id: "next-reservation",
      icon: Clock3,
      label: "رزرو بعدی",
      value: nextReservation?.startTime || nextReservation?.time || "—",
      secondary: nextReservation
        ? `${nextReservation.customerName || "مهمان"} · ${toPersianNumber(nextReservation.partySize || nextReservation.people)} نفر · ${formatReservationDate(nextReservation)}`
        : "رزرو پیش‌رویی نیست",
      to: nextReservation ? "/dashboard/reservations?filter=UPCOMING" : undefined,
    });
  }

  let primaryAction = {
    label: "رزرو جدید",
    to: "/dashboard/reservations?action=new",
    icon: CalendarPlus,
  };
  if (data.capabilities.menus && !data.menu.exists) {
    primaryAction = { label: "ساخت منو", to: "/dashboard/edit-menu", icon: Utensils };
  } else if (data.floorPlan && !data.floorPlan.exists) {
    primaryAction = {
      label: "طراحی سالن",
      to: "/dashboard/reservations?section=FLOOR_PLAN",
      icon: LayoutTemplate,
    };
  }

  const oddKpis = kpis.length % 2 === 1;

  return (
    <main className="mx-auto max-w-[90rem] space-y-4 pb-6" dir="rtl">
      {/* 1. Header: where am I, what day is it, the one thing I can do now */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-accent">نمای امروز</h1>
          <p className="mt-0.5 text-sm text-gray-500">{todayFormatter.format(new Date())}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            title="بروزرسانی"
            aria-label={refreshing ? "در حال بروزرسانی" : "بروزرسانی"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-accent focus:outline-none focus:ring-4 focus:ring-gray-100 disabled:cursor-wait disabled:opacity-60"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} aria-hidden="true" />
          </button>
          <HeaderAction label="مدیریت میزها" to="/dashboard/reservations?section=SETTINGS" icon={TableProperties} />
          <HeaderAction {...primaryAction} primary />
        </div>
      </header>

      {status === "partial" && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-100 bg-amber-50/70 px-3 py-2 text-xs text-amber-800" role="status">
          <AlertCircle size={16} className="shrink-0" aria-hidden="true" />
          <p>
            بخشی از اطلاعات در دسترس نیست ({toPersianNumber(Object.keys(errors).length)} سرویس). اطلاعات موجود نمایش داده شده است.
          </p>
        </div>
      )}

      {/* 2. Setup guide — only until setup is finished; names one next step */}
      <OnboardingChecklist onboarding={data.onboarding} />

      {/* 3. Today at a glance — one strip instead of five tall cards */}
      {kpis.length > 0 && (
        <section aria-label="آمار امروز">
          <div
            className={`grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 ${statColumns[kpis.length] || "md:grid-cols-5"}`}
          >
            {kpis.map((kpi, index) => (
              <DashboardKpiCard
                key={kpi.id}
                {...kpi}
                className={oddKpis && index === kpis.length - 1 ? "col-span-2 md:col-span-1" : ""}
              />
            ))}
          </div>
        </section>
      )}

      {/* 4. Work area: live orders on the main side, status cards on the side */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <RecentOrdersSection
          orders={data.recent.orders}
          loading={resourceLoading.orders}
          error={errors.orders}
          actionError={actionState.scope === "order" ? actionState.error : null}
          busyKey={actionState.busyKey}
          onRetry={refresh}
          onStatusChange={actions.updateOrderStatus}
        />

        <aside className="space-y-4">
          <AttentionPanel items={data.attentionItems} />
          <MenuStatusSection menu={data.menu} menuLink={data.restaurant.menuLink} error={errors.menus} />
          <SubscriptionStatusCard subscription={data.restaurant.subscription} error={errors.subscription} />
        </aside>
      </div>
    </main>
  );
}
